// src/components/roleplay/LiveCall.js
// The live call screen: rep talks (voice or typed) to an AI prospect driven
// by roleplayTurn(). Keeps a running transcript and hands it off to the
// scoring step once the rep ends the call.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { roleplayTurn } from '../../data/roleplay';
import { trackLabel, difficultyLabel } from '../../config/roleplay';
import useSpeech from './useSpeech';

const CallHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
`;

const PersonaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PersonaName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
`;

const PersonaMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const Badges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #0e8c6a;
  background: #e6f5f0;
  white-space: nowrap;
`;

const TranscriptPanel = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  min-height: 320px;
  max-height: 480px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
`;

const EmptyTranscript = styled.div`
  margin: auto;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
`;

const BubbleRow = styled.div`
  display: flex;
  justify-content: ${(props) => (props.$speaker === 'rep' ? 'flex-end' : 'flex-start')};
`;

const Bubble = styled.div`
  max-width: 72%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  ${(props) =>
    props.$speaker === 'rep'
      ? `
        background: #0e8c6a;
        color: #ffffff;
        border-bottom-right-radius: 4px;
      `
      : `
        background: #f1f5f9;
        color: #1e293b;
        border-bottom-left-radius: 4px;
      `}
`;

const ThinkingRow = styled(BubbleRow)`
  justify-content: flex-start;
`;

const ThinkingBubble = styled(Bubble)`
  background: #f1f5f9;
  color: #94a3b8;
  font-style: italic;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  min-height: 18px;
`;

const TurnNote = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.$awaiting ? '#94a3b8' : '#0e8c6a')};
`;

const SttNote = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;

const ErrorBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 12px;
`;

const RetryButton = styled.button`
  appearance: none;
  border: 1px solid #dc2626;
  background: #ffffff;
  color: #dc2626;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #fef2f2;
  }
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HandsFreeToggle = styled.button`
  appearance: none;
  align-self: flex-start;
  border: 1px solid ${(props) => (props.$on ? '#0e8c6a' : '#e2e8f0')};
  background: ${(props) => (props.$on ? '#e6f5f0' : '#ffffff')};
  color: ${(props) => (props.$on ? '#0e8c6a' : '#64748b')};
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0e8c6a;
  }
`;

const ComposerRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const MicPulse = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #dc2626;
  animation: micpulse 1s ease-in-out infinite;

  @keyframes micpulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.75); }
  }
`;

const MicButton = styled.button`
  appearance: none;
  border: 1px solid ${(props) => (props.$active ? '#0e8c6a' : '#e2e8f0')};
  background: ${(props) => (props.$active ? '#e6f5f0' : '#ffffff')};
  color: ${(props) => (props.$active ? '#0e8c6a' : '#64748b')};
  width: 44px;
  height: 44px;
  border-radius: 10px;
  font-size: 18px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #0e8c6a;
    color: #0e8c6a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextInput = styled.input`
  flex: 1;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #0e8c6a;
    box-shadow: 0 0 0 3px rgba(14, 140, 106, 0.12);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
  }
`;

const SendButton = styled.button`
  appearance: none;
  border: none;
  background: #0e8c6a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  padding: 0 18px;
  height: 44px;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #0b7057;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const EndButton = styled.button`
  appearance: none;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: 10px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #0e8c6a;
    color: #0e8c6a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function LiveCall({ scenario, persona, user, onEnd }) {
  const {
    sttSupported,
    isListening,
    isSpeaking,
    startListening,
    stopListening,
    speak,
    cancelSpeak,
  } = useSpeech();

  const [transcript, setTranscript] = useState([]);
  const [inputText, setInputText] = useState('');
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [error, setError] = useState(null);
  const [pendingRetry, setPendingRetry] = useState(null);
  const [handsFree, setHandsFree] = useState(sttSupported);

  // Refs mirror state so the async speech callbacks (which capture a stale
  // closure) always read the live values.
  const usageRef = useRef({ inputTokens: 0, outputTokens: 0 });
  const transcriptRef = useRef([]);
  const awaitingRef = useRef(false);
  const handsFreeRef = useRef(sttSupported);
  const callActiveRef = useRef(true);
  const gotResultRef = useRef(false);
  const submitLineRef = useRef(null);
  const beginListeningRef = useRef(null);

  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => { awaitingRef.current = awaitingReply; }, [awaitingReply]);
  useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript, awaitingReply, isSpeaking]);

  // Open the mic for the rep's turn. In hands-free mode, if the rep goes quiet
  // (recognition ends with no result), reopen it so the call stays continuous.
  const beginListening = useCallback(() => {
    if (!sttSupported || !handsFreeRef.current || !callActiveRef.current) return;
    if (awaitingRef.current) return;
    gotResultRef.current = false;
    startListening(
      (finalText) => {
        gotResultRef.current = true;
        stopListening();
        submitLineRef.current?.(finalText);
      },
      (err) => {
        // Mic access blocked → stop the loop and fall back to typing, rather
        // than spinning on failed restarts.
        if (err === 'not-allowed' || err === 'service-not-allowed') {
          handsFreeRef.current = false;
          setHandsFree(false);
          setError('Microphone is blocked. Allow mic access in your browser, then toggle Hands-free back on — or just type your lines.');
          return;
        }
        // Otherwise (silence / no-speech) keep the mic open for a real-call feel.
        if (
          !gotResultRef.current &&
          callActiveRef.current &&
          handsFreeRef.current &&
          !awaitingRef.current
        ) {
          window.setTimeout(() => beginListeningRef.current?.(), 300);
        }
      }
    );
  }, [sttSupported, startListening, stopListening]);
  useEffect(() => { beginListeningRef.current = beginListening; }, [beginListening]);

  const requestReply = useCallback(
    (historySnapshot, repMessage) => {
      setAwaitingReply(true);
      awaitingRef.current = true;
      setError(null);
      roleplayTurn({
        personaId: scenario.personaId,
        difficulty: scenario.difficulty,
        history: historySnapshot,
        repMessage,
      })
        .then((res) => {
          const reply = (res && res.reply) || '';
          if (res && res.usage) {
            usageRef.current = {
              inputTokens: usageRef.current.inputTokens + (res.usage.inputTokens || 0),
              outputTokens: usageRef.current.outputTokens + (res.usage.outputTokens || 0),
            };
          }
          setTranscript((prev) => [...prev, { speaker: 'prospect', text: reply }]);
          setAwaitingReply(false);
          awaitingRef.current = false;
          setPendingRetry(null);
          // Speak the reply aloud, then hand the mic back to the rep — this is
          // what makes it feel like a real, continuous call. speak() invokes
          // the callback even when TTS is unsupported, so the loop still runs.
          speak(reply, () => {
            if (callActiveRef.current && handsFreeRef.current) beginListeningRef.current?.();
          });
        })
        .catch((err) => {
          console.error('roleplayTurn failed:', err);
          setAwaitingReply(false);
          awaitingRef.current = false;
          setError(err?.message || "Couldn't reach the prospect. Your line is saved — try again.");
          setPendingRetry({ history: historySnapshot, repMessage });
        });
    },
    [scenario, speak]
  );

  const submitRepLine = useCallback(
    (rawText) => {
      const text = (rawText || '').trim();
      if (!text || awaitingRef.current) return;
      const historySnapshot = transcriptRef.current;
      setTranscript((prev) => [...prev, { speaker: 'rep', text }]);
      setInputText('');
      requestReply(historySnapshot, text);
    },
    [requestReply]
  );
  useEffect(() => { submitLineRef.current = submitRepLine; }, [submitRepLine]);

  // Kick off the call: the rep speaks first (cold open), so open the mic on
  // mount when hands-free. A small delay lets the mic-permission UI settle.
  useEffect(() => {
    callActiveRef.current = true;
    let t;
    if (sttSupported && handsFreeRef.current) {
      t = window.setTimeout(() => beginListeningRef.current?.(), 400);
    }
    return () => {
      if (t) window.clearTimeout(t);
    };
  }, [sttSupported]);

  // Tear down mic + TTS when leaving the call.
  useEffect(
    () => () => {
      callActiveRef.current = false;
      stopListening();
      cancelSpeak();
    },
    [stopListening, cancelSpeak]
  );

  function toggleHandsFree() {
    setHandsFree((prev) => {
      const next = !prev;
      handsFreeRef.current = next;
      if (next) {
        if (!awaitingRef.current && !isSpeaking) beginListeningRef.current?.();
      } else {
        stopListening();
      }
      return next;
    });
  }

  function handlePushToTalk() {
    if (isListening) {
      stopListening();
      return;
    }
    gotResultRef.current = false;
    startListening((finalText) => {
      gotResultRef.current = true;
      stopListening();
      submitRepLine(finalText);
    });
  }

  function handleRetry() {
    if (!pendingRetry) return;
    requestReply(pendingRetry.history, pendingRetry.repMessage);
  }

  function handleSubmit(e) {
    e.preventDefault();
    stopListening(); // typing takes over this turn from the mic
    submitRepLine(inputText);
  }

  function handleEnd() {
    callActiveRef.current = false;
    stopListening();
    cancelSpeak();
    onEnd(transcriptRef.current, usageRef.current);
  }

  const repTurns = transcript.filter((t) => t.speaker === 'rep').length;
  const prospectTurns = transcript.filter((t) => t.speaker === 'prospect').length;
  const canEnd = repTurns >= 1 && prospectTurns >= 1 && !awaitingReply;

  let statusText = 'Your turn — speak or type';
  if (awaitingReply) statusText = 'Prospect is thinking…';
  else if (isSpeaking) statusText = 'Prospect is speaking…';
  else if (isListening) statusText = 'Listening — go ahead';
  const statusBusy = awaitingReply || isSpeaking;

  return (
    <div>
      <CallHeader>
        <PersonaBlock>
          <PersonaName>{persona?.title || 'Prospect'}</PersonaName>
          <PersonaMeta>{persona?.company || 'Unknown company'}</PersonaMeta>
        </PersonaBlock>
        <Badges>
          <Badge>{trackLabel(scenario.track)}</Badge>
          <Badge>{difficultyLabel(scenario.difficulty)}</Badge>
        </Badges>
      </CallHeader>

      <StatusBar>
        <TurnNote $awaiting={statusBusy}>
          {isListening && !statusBusy && <MicPulse />} {statusText}
        </TurnNote>
        {!sttSupported && <SttNote>Voice needs Chrome — you can type instead.</SttNote>}
      </StatusBar>

      {error && (
        <ErrorBar>
          <span>{error}</span>
          <RetryButton type="button" onClick={handleRetry}>
            Retry
          </RetryButton>
        </ErrorBar>
      )}

      <TranscriptPanel ref={scrollRef}>
        {transcript.length === 0 && !awaitingReply && (
          <EmptyTranscript>
            {sttSupported && handsFree
              ? 'The line is ringing — just start talking with your opener.'
              : 'Say hello to get the call started — speak or type your opener below.'}
          </EmptyTranscript>
        )}
        {transcript.map((turn, i) => (
          <BubbleRow key={i} $speaker={turn.speaker}>
            <Bubble $speaker={turn.speaker}>{turn.text}</Bubble>
          </BubbleRow>
        ))}
        {awaitingReply && (
          <ThinkingRow $speaker="prospect">
            <ThinkingBubble $speaker="prospect">prospect is thinking…</ThinkingBubble>
          </ThinkingRow>
        )}
      </TranscriptPanel>

      <Composer>
        {sttSupported && (
          <HandsFreeToggle type="button" $on={handsFree} onClick={toggleHandsFree}>
            {handsFree ? '🎙 Hands-free: On' : '🎙 Hands-free: Off'}
          </HandsFreeToggle>
        )}
        <form onSubmit={handleSubmit}>
          <ComposerRow>
            {sttSupported && !handsFree && (
              <MicButton
                type="button"
                $active={isListening}
                disabled={awaitingReply}
                onClick={handlePushToTalk}
                aria-label={isListening ? 'Stop listening' : 'Push to talk'}
                title={isListening ? 'Stop listening' : 'Push to talk'}
              >
                {isListening ? '●' : '🎤'}
              </MicButton>
            )}
            <TextInput
              type="text"
              placeholder={
                awaitingReply
                  ? 'Waiting for the prospect…'
                  : handsFree
                  ? 'Or type a line…'
                  : 'Type your line…'
              }
              value={inputText}
              disabled={awaitingReply}
              onChange={(e) => setInputText(e.target.value)}
            />
            <SendButton type="submit" disabled={awaitingReply || !inputText.trim()}>
              Send
            </SendButton>
          </ComposerRow>
        </form>
      </Composer>

      <FooterRow>
        <EndButton type="button" disabled={!canEnd} onClick={handleEnd}>
          End call &amp; get feedback
        </EndButton>
      </FooterRow>
    </div>
  );
}

export default LiveCall;
